import { useEffect, useState, Fragment } from "react";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { GetDoc } from "@/Utils/StateLib";
import { GetChartMetadata } from "@/Utils/Promises";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Modal,
  Flex,
  Input,
  Select,
  Divider,
  isNullish,
  Alert,
  Loader
} from "@theorgchartdev/storybook";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import SectionPrompt from "@/appComponents/Blocks/Composites/SectionPrompt/SectionPrompt";
import { NumericInputField } from "@/appComponents/Blocks/Composites/ReactInputFields/NumericInputField/NumericInputField";
import { GetFieldDefFromName } from "@/Utils/DataLib";
import { appstr } from "@coreapp/utils/appstr";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import {
  kHeadcount,
  kBudgetedHeadcount,
  kSalaryRollup,
  kPositionBudgetRollup,
  kPositionCount,
  kSubordinatePositions,
  kDirectSubordinatePositions,
  kPeerPositions,
  enumObjClassNames
} from "@coreapp/constants";
import cx from "classnames";
import { useStatusController } from "@/hooks/useStatusController";
import { getObjClassNameForField } from "@/Utils/Plan/MetricsUtils";

const POSITION_ONLY_FIELDS = [
  kBudgetedHeadcount,
  kSalaryRollup,
  kPositionBudgetRollup,
  kSubordinatePositions,
  kDirectSubordinatePositions,
  kPeerPositions
];
const MAX_METRICS = 5; // 4 + headcount

export const ManageMetricsModal = () => {
  const {
    planManager,
    manageMetricsContextValue: { showManageMetrics, onSaveMetrics, onCloseManageMetrics }
  } = usePlanManagerContext();

  const planMgr = planManager.state;
  const dataTestIdPrefix = "metrics-modal-";
  const [metricsData, setMetricsData] = useState({ data: [] });
  const [chartMetadata, setChartMetadata] = useState(null);
  const doc = GetDoc();
  const objClassName = doc?.IsPositionDocument()
    ? enumObjClassNames.position
    : enumObjClassNames.box;
  const chartID = doc?.GetChartID();
  const statusController = useStatusController();

  useEffect(() => {
    let active = true;
    if (chartID) {
      setChartMetadata(null);
      statusController.performUpdate(async () => {
        try {
          const { result } = await GetChartMetadata(chartID);
          if (active) {
            setChartMetadata(result);
          }
        } catch (error) {
          if (active) {
            setChartMetadata(null);
          }
        }
      }, appstr("Loading"));
    } else {
      setChartMetadata(null);
      statusController.setReady();
    }
    return () => {
      active = false;
    };
  }, [chartID]);

  const ValidateMetrics = () => {
    const checkFields = metricsData.data.map(function (item) {
      return item["fieldID"];
    });
    return (
      checkFields.indexOf("") === -1 &&
      checkFields.indexOf(undefined) === -1 &&
      checkFields.indexOf(null) === -1
    );
  };

  const onSave = () => {
    if (ValidateMetrics()) onSaveMetrics(metricsData.data);
  };

  const GetMetrics = () => {
    let planInfo = planMgr?.GetPlanInfo();
    if (!planMgr || !planInfo) return [];
    return JSON.parse(JSON.stringify(planInfo["metrics"]));
  };

  useEffect(() => {
    if (!planMgr && !showManageMetrics) {
      return;
    }
    setMetricsData({ data: GetMetrics() });
  }, [showManageMetrics]);

  const handleMetricTargetChange = (metricIdx, newValue) => {
    // update target value
    let currentMetric = metricsData.data[metricIdx];
    currentMetric["targetValue"] = newValue;

    // update metrics list
    let existingMetrics = [...metricsData.data];
    existingMetrics[metricIdx] = currentMetric;

    // update state
    setMetricsData({ data: [...existingMetrics] });
  };

  const removeMetric = metricIdx => {
    // update metrics list
    let existingMetrics = [...metricsData.data];
    existingMetrics.splice(metricIdx, 1);

    // update state
    setMetricsData({ data: [...existingMetrics] });
  };

  const addMetric = () => {
    let existingMetrics = [...metricsData.data];

    // Find the first available formula that's not already selected
    const currentMetricFields = existingMetrics.map(item => item.fieldID);
    const allFormulaFields = getFormulaFields();
    const firstAvailableFormula = allFormulaFields.find(
      field => !currentMetricFields.includes(field)
    );

    // Create new metric with the first available formula pre-selected
    let newMetric = {
      fieldID: firstAvailableFormula || "",
      targetValue: ""
    };
    existingMetrics.push(newMetric);
    // update state
    setMetricsData({ data: [...existingMetrics] });
  };

  const onFieldSelect = option => {
    let currentMetric = metricsData.data[option.metricIndex];
    currentMetric["fieldID"] = option.value;

    // update metrics list
    let existingMetrics = [...metricsData.data];
    existingMetrics[option.metricIndex] = currentMetric;

    // update state
    setMetricsData({ data: [...existingMetrics] });
  };

  const getFormulaFields = () => {
    const isPositionChart = doc?.IsPositionDocument();
    const countField = isPositionChart ? kPositionCount : kHeadcount;
    let formulaFields = [countField]; // count field is always available and can't be removed

    if (isPositionChart) {
      // add position only fields
      formulaFields = formulaFields.concat(POSITION_ONLY_FIELDS);
    }

    // add static formulas from loaded chart metadata
    const formulas = chartMetadata?.["formulas"] ?? [];
    const staticFormulaNames = formulas.map(f => f.name);
    formulaFields = formulaFields.concat(staticFormulaNames.sort());

    return formulaFields;
  };

  const BuildFieldOptions = (idx, selectedField) => {
    let fieldOptions = [];
    let doc = GetDoc();
    if (!doc) return fieldOptions;

    const curMetricFields = metricsData.data.map(function (item) {
      return item.fieldID;
    });

    const isPositionChart = doc?.IsPositionDocument();
    const countField = isPositionChart ? kPositionCount : kHeadcount;
    const allFormulaFields = getFormulaFields();
    allFormulaFields.forEach(field => {
      // disable if not the current selected field or if it's the primary count field
      const disabled =
        field === countField || (selectedField !== field && curMetricFields.indexOf(field) > -1);
      fieldOptions.push({
        value: field,
        label: field,
        metricIndex: idx,
        disabled: disabled,
        dataTestId: `${dataTestIdPrefix}list-${field}`,
        clickHandler: onFieldSelect
      });
    });
    return fieldOptions;
  };

  const isLimitReached = () => {
    if (!metricsData || !metricsData.data) return false;
    return metricsData.data.length >= MAX_METRICS;
  };

  const isAvailableFormulas = () => {
    if (!metricsData || !metricsData.data) return false;

    const currentMetricFields = metricsData.data.map(item => item.fieldID);
    const formulasAvailable = getFormulaFields().filter(
      field => !currentMetricFields.includes(field)
    );
    return formulasAvailable.length !== 0;
  };

  const canAddMoreMetrics = () => {
    if (isLimitReached()) return false;
    return isAvailableFormulas();
  };

  const getAddBtnToolTipProps = () => {
    if (isLimitReached()) {
      return { message: appstr("Remove a metric to add a new one") };
    }
    if (!isAvailableFormulas()) {
      return { message: appstr("No metrics available to add") };
    }
    return undefined;
  };

  const getAlertData = () => {
    if (isLimitReached()) {
      return createBoldToastMessage("Maximum number of metrics reached alert", {
        MAX_METRICS: MAX_METRICS.toString(),
        metrics: appstr("Metrics").toLowerCase()
      });
    }
    if (!isAvailableFormulas()) {
      return createBoldToastMessage("No more formulas available to add alert", {
        chartSettingsFieldsFormulasPath: appstr("chartSettingsFieldsFormulasPath")
      });
    }
    return null;
  };

  const disableMetricsAdd = !canAddMoreMetrics();
  const alertData = getAlertData();

  return (
    <CentralizedModalContainer show={showManageMetrics}>
      <Modal
        id="example-modal"
        show={showManageMetrics}
        onClose={onCloseManageMetrics}
        minWidth={"large"}
        maxWidth={"large"}
        withShadow={true}
        centerScreen={true}
      >
        <DialogTitle divider={true} onClose={onCloseManageMetrics}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"chart-line"} />
            {appstr("Manage metrics")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content"}>
          <Loader.Root>
            <Flex direction={"column"} gap={"16px"}>
              {alertData && (
                <Alert
                  severity={"info"}
                  className={cx("oc-padding-small", "oc-text-small")}
                  {...alertData.alertProps}
                >
                  {alertData.message}
                </Alert>
              )}
              <SectionPrompt description={appstr("Add metrics info")}>
                <div style={{ display: "inline-block" }}>
                  <Button
                    dataTestId={"manage-metrics-create-btn"}
                    onClick={addMetric}
                    modifier={"secondary"}
                    size={"medium"}
                    disabled={disableMetricsAdd}
                    iconProps={{ name: "plus" }}
                    tooltipProps={getAddBtnToolTipProps()}
                  >
                    {appstr("Add metric")}
                  </Button>
                </div>
              </SectionPrompt>
              {doc &&
                metricsData.data.map((metric, idx) => {
                  const targetValue =
                    isNaN(metric.targetValue) || isNullish(metric.targetValue)
                      ? 0
                      : metric.targetValue;

                  return (
                    <Fragment key={idx}>
                      <Flex direction={"column"} gap={"8px"}>
                        <ActionListItem
                          key={idx}
                          visible="always"
                          contentBefore={
                            <Flex
                              gap={"12px"}
                              width={"100%"}
                              padding={idx === 0 ? "0px 32px 0px 0px" : null}
                              className={"oc-field-input-wrapper"}
                            >
                              <div style={{ flex: "1" }}>
                                <Select
                                  dataTestId={`${dataTestIdPrefix}formula-select`}
                                  key={"metric-select-" + idx}
                                  value={metric.fieldID}
                                  inputOnly={idx !== 0}
                                  label={idx !== 0 ? null : appstr("Target field")}
                                  placeholder={appstr("Choose field")}
                                  options={BuildFieldOptions(idx, metric.fieldID)}
                                  disabled={idx === 0}
                                  selectSize={"medium"}
                                />
                              </div>
                              <div style={{ flex: "1" }}>
                                <NumericInputField
                                  fieldDef={GetFieldDefFromName(
                                    metric.fieldID,
                                    getObjClassNameForField(
                                      metric.fieldID,
                                      chartMetadata,
                                      objClassName
                                    )
                                  )}
                                  customInput={Input}
                                  data-testid={`${dataTestIdPrefix}value-input`}
                                  key={"metric-input-" + idx}
                                  size={"medium"}
                                  value={targetValue}
                                  inputOnly={idx !== 0}
                                  label={appstr("Target value")}
                                  returnNumber
                                  onChange={(_, value) => handleMetricTargetChange(idx, value)}
                                  placeholder={appstr("Enter Target Value")}
                                  className={"oc-input-flex"}
                                />
                              </div>
                            </Flex>
                          }
                          actions={
                            idx > 0
                              ? [
                                  {
                                    onClick: () => removeMetric(idx),
                                    iconName: "trash-can",
                                    iconProps: {
                                      tooltipProps: { message: appstr("Delete metric") },
                                      color: "error"
                                    }
                                  }
                                ]
                              : []
                          }
                        />
                        <Divider />
                      </Flex>
                    </Fragment>
                  );
                })}
              {statusController.isLoading && (
                <Loader.Container dataTestId="manage-metrics-loader">
                  <Loader.Spinner />
                  <Loader.Text>{statusController.message ?? appstr("Loading")}</Loader.Text>
                </Loader.Container>
              )}
            </Flex>
          </Loader.Root>
        </DialogContent>
        {
          <DialogActions divider={true}>
            <DialogActions.Tertiary
              dataTestId={`${dataTestIdPrefix}cancel-btn`}
              onClick={onCloseManageMetrics}
            >
              {appstr("Cancel")}
            </DialogActions.Tertiary>
            <DialogActions.Primary dataTestId={`${dataTestIdPrefix}save-btn`} onClick={onSave}>
              {appstr("Save")}
            </DialogActions.Primary>
          </DialogActions>
        }
      </Modal>
    </CentralizedModalContainer>
  );
};
